import type { BaseEffect, EffectResult } from "./effect";
import type { Buff } from "./buff";
import type { Pet } from "./pet";
import type { Skill } from "./skill";
import type { BattleEvent, ActionContext } from "./events";
import type { Timing } from "./timing";
import type { Side } from "./operation";
import type { Seer } from "./seer";
import { AbnormalState } from "./state";

/* ---------- 条件上下文与类型 ---------- */

/**
 * 效果触发器与条件谓词共用的上下文。
 *
 * 条件只依赖本次效果结算所需的战斗上下文；子效果的父效果结果由
 * `Timing.triggered` / `Timing.untriggered` 在效果派发阶段处理。
 */
export interface Context {
    /** 当前调用方接收实际业务效果的结果；不保存到精灵或快照。 */
    onEffectResult?: (effect: BaseEffect, result: EffectResult, context: Context) => void;
    /** 效果持有者与事件主体分离，供背包光环判断实际行动者。 */
    eventPet?: Pet;
    /**
     * eventTarget，保存当前实例的eventTarget状态。
     */
    eventTarget?: Pet;
    /**
     * eventSeer，保存当前实例的eventSeer状态。
     */
    eventSeer?: Seer;
    /**
     * eventSide，保存当前实例的eventSide状态。
     */
    eventSide?: Side;
    /**
     * buff，保存当前实例的buff状态。
     */
    buff?: Buff;

    /** 排序查询的累加先制，不写入持有者持久状态。 */
    priorityBonus?: number;
    /**
     * skill，保存当前实例的skill状态。
     */
    skill?: Skill;
    /**
     * action，保存当前实例的action状态。
     */
    action?: ActionContext;
    /**
     * timing，保存当前实例的timing状态。
     */
    timing?: Timing;
    /**
     * side，保存当前实例的side状态。
     */
    side?: Side;
    /**
     * events，保存当前实例的events状态。
     */
    events?: BattleEvent[];
    /** 发起效果的一方精灵。 */
    sbjPet: Pet;
    /** 承受效果的一方精灵。 */
    objPet: Pet;
    /** 发起效果一方的赛尔。 */
    sbjSeer: Seer;
    /** 承受效果一方的赛尔。 */
    objSeer: Seer;
}

/** 接收完整效果上下文并返回条件结果的函数。 */
export type Condition = (context: Context) => boolean;

/* ---------- 概率条件 ---------- */

/**
 * 创建一个按百分比独立判定的条件。
 *
 * @param x 触发概率百分比，取值范围为 0 到 100
 * @returns 接收效果上下文并返回本次判定结果的条件
 * @throws {RangeError} 当概率不是 0 到 100 之间的有限数值时抛出
 */
export function 百分之x的几率(x: number): Condition {
    if (!Number.isFinite(x) || x < 0 || x > 100) {
        throw new RangeError("概率百分比必须是 0 到 100 之间的有限数值");
    }
    return ({ sbjSeer }) => sbjSeer.random.next() < x / 100;
}

/* ---------- 条件组合器 ---------- */

/**
 * 创建条件“或”组合器。
 *
 * @param conditions 待组合的条件；任一条件满足即返回 `true`
 * @returns 接收效果上下文的组合条件
 */
export function or(...conditions: Condition[]): Condition {
    return (context) => conditions.some((condition) => condition(context));
}

/**
 * 创建条件“与”组合器。
 *
 * @param conditions 待组合的条件；所有条件满足才返回 `true`
 * @returns 接收效果上下文的组合条件
 */
export function and(...conditions: Condition[]): Condition {
    return (context) => conditions.every((condition) => condition(context));
}

/**
 * 创建条件“非”组合器。
 *
 * @param condition 待取反的条件
 * @returns 接收效果上下文的取反条件
 */
export function not(condition: Condition): Condition {
    return (context) => !condition(context);
}

/* ---------- 常用条件 ---------- */

/** 判断自身是否处于强化状态。 */
export const 若自身处于强化状态: Condition = ({ sbjPet }) => {
    return sbjPet.能力等级.isLevelUp();
};

/** 判断自身是否未处于强化状态。 */
export const 若自身不处于强化状态: Condition = ({ sbjPet }) => {
    return !sbjPet.能力等级.isLevelUp();
};

/**
 * 若对手处于强化状态，供本模块相关规则使用。
 */
export const 若对手处于强化状态: Condition = ({ objPet }) => {
    return objPet.能力等级.isLevelUp();
};

/**
 * 若对手不处于强化状态，供本模块相关规则使用。
 */
export const 若对手不处于强化状态: Condition = ({ objPet }) => {
    return !objPet.能力等级.isLevelUp();
};

/** 判断自身是否处于弱化状态。 */
export const 若自身处于弱化状态: Condition = ({ sbjPet }) => {
    return sbjPet.能力等级.isLevelDown();
};

/** 判断自身是否未处于弱化状态。 */
export const 若自身不处于弱化状态: Condition = ({ sbjPet }) => {
    return !sbjPet.能力等级.isLevelDown();
};

/** 判断对手是否处于弱化状态。 */
export const 若对手处于弱化状态: Condition = ({ objPet }) => {
    return objPet.能力等级.isLevelDown();
};

/** 判断对手是否未处于弱化状态。 */
export const 若对手不处于弱化状态: Condition = ({ objPet }) => {
    return !objPet.能力等级.isLevelDown();
};

/** 判断本次攻击是否打出致命一击。 */
export const 若打出致命一击: Condition = ({ sbjPet }) => {
    return sbjPet.criticalStrike === true;
};

/** 判断自身当前体力是否高于对手。 */
export const 若当前体力高于对手: Condition = ({ sbjPet, objPet }) => {
    return sbjPet.当前体力 > objPet.当前体力;
};

/** 判断自身当前体力是否低于对手。 */
export const 若当前体力低于对手: Condition = ({ sbjPet, objPet }) => {
    return sbjPet.当前体力 < objPet.当前体力;
};

/** 判断对手是否为自身的天敌。 */
export const 若对手为自身的天敌: Condition = ({ sbjPet, objPet }) => {
    return objPet.属性.getScale(sbjPet.属性) > 1;
};

/** 判断对手是否处于至少一种异常状态。 */
export const 若对手处于异常状态: Condition = ({ objPet }) => {
    return objPet.异常状态.some((state) => state instanceof AbnormalState);
};

/** 判断对手是否未处于任何异常状态。 */
export const 若对手不处于异常状态: Condition = ({ objPet }) => {
    return !objPet.异常状态.some((state) => state instanceof AbnormalState);
};

/** 判断自身是否先出手。 */
export const 若先出手: Condition = ({ sbjSeer }) => {
    return sbjSeer.先出手 === true;
};

/** 判断自身是否后出手。 */
export const 若后出手: Condition = ({ sbjSeer }) => {
    return sbjSeer.先出手 === false;
};

export const 若未击败对手: Condition = ({ objPet }) => {
    return objPet.isAlive();
}

/* ---------- 参数化条件 ---------- */

/**
 * 创建“自身当前体力低于最大体力的 1/x”条件。
 *
 * @param x 最大体力的分母
 * @returns 体力阈值条件
 */
export function 若自身体力低于x分之一(x: number): Condition {
    return ({ sbjPet }) => sbjPet.当前体力 < sbjPet.最大体力 / x;
}

/**
 * 创建“自身当前体力高于最大体力的 1/x”条件。
 *
 * @param x 最大体力的分母
 * @returns 体力阈值条件
 */
export function 若自身体力高于x分之一(x: number): Condition {
    return ({ sbjPet }) => sbjPet.当前体力 > sbjPet.最大体力 / x;
}

/**
 * 创建“本次造成的伤害不低于 x”条件。
 *
 * @param x 伤害下限
 * @returns 伤害阈值条件
 */
export function 造成的伤害超过x(x: number): Condition {
    return ({ objPet }) => {
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = objPet.战斗面板.damage;
        return damage ? damage >= x : false;
    };
}

/**
 * 创建“本次造成的伤害低于 x”条件。
 *
 * @param x 伤害上限（不含）
 * @returns 伤害阈值条件
 */
export function 造成的伤害低于x(x: number): Condition {
    return ({ objPet }) => {
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = objPet.战斗面板.damage;
        return damage ? damage < x : false;
    };
}
